import {useEffect, useState, type ComponentProps, type ReactNode} from 'react';
import {ChevronDown, ChevronUp, Eye, EyeOff, X} from 'lucide-react';
import {Input} from '@/components/ui/input';
import {Button} from '@/components/app/button';
import {Toggle} from '@/components/ui/toggle';
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group';
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover';
import {Calendar} from '@/components/ui/calendar';
import {Icon, type IconProp} from '@/app/components/Icon';
import {cn} from '@/lib/utils';

export interface MaskedInputProps extends Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> {
  mask: string;
  value?: string | null;
  onValueChange?: (value: string) => void;
}
export function applyMask(value: string, mask: string) {
  const characters = Array.from(value);
  let result = '';
  for (const token of mask.replace(/\?/g, '')) {
    const pattern = token === '9' ? /\d/ : token === 'a' ? /[\p{L}]/u : token === '*' ? /[\p{L}\p{N}]/u : null;
    if (!pattern) {
      if (characters[0] === token) characters.shift();
      if (characters.length) result += token;
      continue;
    }
    let next = characters.shift();
    while (next !== undefined && !pattern.test(next)) next = characters.shift();
    if (next === undefined) break;
    result += next;
  }
  return result;
}
export function MaskedInput({mask, value, onValueChange, ...props}: MaskedInputProps) {
  return (
    <Input
      {...props}
      value={value ?? ''}
      maxLength={mask.replace(/\?/g, '').length}
      onChange={(event) => onValueChange?.(applyMask(event.target.value, mask))}
    />
  );
}
export interface NumberInputProps extends Omit<
  ComponentProps<typeof Input>,
  'value' | 'onChange' | 'min' | 'max' | 'step'
> {
  value?: number | null;
  onValueChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  useGrouping?: boolean;
  minFractionDigits?: number;
  maxFractionDigits?: number;
  fractionDigits?: number;
  showButtons?: boolean;
  inputClassName?: string;
  inputId?: string;
  locale?: string;
}
export function NumberInput({
  value,
  onValueChange,
  min,
  max,
  step = 1,
  useGrouping = false,
  minFractionDigits,
  maxFractionDigits,
  fractionDigits,
  showButtons,
  inputClassName,
  className,
  inputId,
  id,
  onBlur,
  locale,
  ...props
}: NumberInputProps) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  useEffect(() => {
    setDraft(value == null ? '' : String(value));
  }, [value]);
  const commit = (next: number | null) =>
    onValueChange?.(next == null ? null : Math.min(max ?? Infinity, Math.max(min ?? -Infinity, next)));
  const input = (
    <Input
      {...props}
      id={inputId ?? id}
      className={cn('min-w-0', showButtons && 'pr-9', inputClassName, !showButtons && className)}
      inputMode="decimal"
      value={draft}
      onChange={(event) => {
        const text = event.target.value.replace(/,/g, '');
        if (!/^-?\d*(\.\d*)?$/.test(text)) return;
        setDraft(text);
        const number = Number(text);
        onValueChange?.(text === '' || text === '-' ? null : Number.isFinite(number) ? number : null);
      }}
      onBlur={(event) => {
        if (draft !== '' && Number.isFinite(Number(draft))) {
          const next = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, Number(draft)));
          const digits = fractionDigits ?? maxFractionDigits;
          const rounded = digits === undefined ? next : Number(next.toFixed(digits));
          commit(rounded);
          setDraft(
            useGrouping
              ? new Intl.NumberFormat(locale, {
                  minimumFractionDigits: minFractionDigits,
                  maximumFractionDigits: digits,
                }).format(rounded)
              : String(rounded),
          );
        }
        onBlur?.(event);
      }}
    />
  );
  // Compact stepper inside the field's right edge, so the control stays as narrow as a plain input.
  return showButtons ? (
    <div className={cn('relative inline-flex items-center', className)}>
      {input}
      <div className="absolute inset-y-0.5 right-3 flex w-6 flex-col">
        <button
          type="button"
          aria-label="Increase"
          disabled={props.disabled}
          className="flex flex-1 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
          onClick={() => commit((value ?? 0) + step)}
        >
          <ChevronUp className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label="Decrease"
          disabled={props.disabled}
          className="flex flex-1 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
          onClick={() => commit((value ?? 0) - step)}
        >
          <ChevronDown className="size-3.5" />
        </button>
      </div>
    </div>
  ) : (
    input
  );
}
export interface PasswordInputProps extends ComponentProps<typeof Input> {
  inputClassName?: string;
  inputId?: string;
  toggleMask?: boolean;
  feedback?: boolean;
  promptLabel?: string;
  weakLabel?: string;
  mediumLabel?: string;
  strongLabel?: string;
}
export function PasswordInput({
  inputClassName,
  inputId,
  id,
  className,
  toggleMask = true,
  feedback,
  promptLabel,
  weakLabel,
  mediumLabel,
  strongLabel,
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <div className={cn('relative', className)}>
      <Input
        {...props}
        id={inputId ?? id}
        type={visible ? 'text' : 'password'}
        className={cn('pr-10', inputClassName)}
      />
      {toggleMask && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="absolute top-1/2 right-2 -translate-y-1/2"
          aria-label={visible ? 'Hide password' : 'Show password'}
          onClick={() => setVisible(!visible)}
        >
          {visible ? <EyeOff /> : <Eye />}
        </Button>
      )}
    </div>
  );
}
export interface TokenInputProps extends Omit<ComponentProps<'input'>, 'value' | 'onChange'> {
  value?: string[] | null;
  onValueChange?: (value: string[]) => void;
  addOnBlur?: boolean;
  separator?: string;
  max?: number;
}
export function TokenInput({
  value = [],
  onValueChange,
  addOnBlur = true,
  separator = ',',
  max,
  className,
  onBlur,
  ...props
}: TokenInputProps) {
  const [draft, setDraft] = useState('');
  const tokens = value ?? [];
  const add = (text: string) => {
    const additions = text
      .split(separator)
      .map((s) => s.trim())
      .filter(Boolean);
    if (additions.length) onValueChange?.([...tokens, ...additions].slice(0, max));
    setDraft('');
  };
  return (
    <div
      className={cn(
        'flex min-h-9 flex-wrap items-center gap-1 rounded-3xl border border-input bg-card px-2 py-1',
        className,
      )}
    >
      {tokens.map((token, index) => (
        <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-sm" key={`${index}-${token}`}>
          {token}
          <button
            type="button"
            aria-label={`Remove ${token}`}
            disabled={props.disabled}
            onClick={() => onValueChange?.(tokens.filter((_, i) => i !== index))}
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        {...props}
        className="min-w-20 flex-1 bg-transparent px-2 py-1 outline-none"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => {
          if (addOnBlur) add(event.currentTarget.value);
          onBlur?.(event);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === separator) {
            event.preventDefault();
            add(event.currentTarget.value);
          } else if (event.key === 'Backspace' && !draft) onValueChange?.(tokens.slice(0, -1));
        }}
        onPaste={(event) => {
          const text = event.clipboardData.getData('text');
          if (text.includes(separator)) {
            event.preventDefault();
            add(event.currentTarget.value + text);
          }
        }}
      />
    </div>
  );
}
export interface SegmentedControlProps<T = any, V = any> {
  options?: T[];
  value?: V;
  onValueChange?: (value: V | null) => void;
  optionLabel?: string | ((item: T) => string);
  optionValue?: string;
  itemTemplate?: (item: T) => ReactNode;
  allowEmpty?: boolean;
  className?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  /** How the selected option is highlighted: primary fill (default) or a neutral grey. */
  tone?: 'primary' | 'plain';
}
/** Reads an option's display text from a field name, a function, or `label`/the option itself. */
export function readOptionLabel<T>(item: T, optionLabel?: string | ((item: T) => string)): string {
  if (typeof optionLabel === 'function') return optionLabel(item);
  const record = item as Record<string, unknown> | null;
  return String((optionLabel ? record?.[optionLabel] : (record?.label ?? item)) ?? '');
}
export function SegmentedControl<T, V>({
  options = [],
  value,
  onValueChange,
  optionLabel,
  optionValue,
  itemTemplate,
  allowEmpty = true,
  className,
  disabled,
  tone = 'primary',
  ...props
}: SegmentedControlProps<T, V>) {
  const getValue = (item: any): V => (optionValue ? item[optionValue] : (item?.value ?? item));
  const index = options.findIndex((item) => Object.is(getValue(item), value));
  return (
    <ToggleGroup
      {...props}
      type="single"
      variant="outline"
      className={cn('flex', className)}
      disabled={disabled}
      value={index < 0 ? '' : String(index)}
      onValueChange={(key) => {
        if (key !== '') onValueChange?.(getValue(options[Number(key)]));
        else if (allowEmpty) onValueChange?.(null);
      }}
    >
      {options.map((item: any, i) => (
        <ToggleGroupItem
          key={i}
          value={String(i)}
          className={cn(
            'grow whitespace-nowrap',
            tone === 'plain'
              ? 'data-[state=on]:bg-zinc-200'
              : 'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary/90',
          )}
        >
          {itemTemplate?.(item) ?? readOptionLabel(item, optionLabel)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
export interface ToggleFieldProps {
  checked?: boolean;
  onCheckedChange?: (value: boolean) => void;
  disabled?: boolean;
  onLabel?: string;
  offLabel?: string;
  onIcon?: IconProp;
  offIcon?: IconProp;
  className?: string;
  hint?: string;
  id?: string;
}
export function ToggleField({
  checked = false,
  onCheckedChange,
  disabled,
  onLabel,
  offLabel,
  onIcon,
  offIcon,
  className,
  hint,
  id,
}: ToggleFieldProps) {
  const icon = checked ? onIcon : offIcon;
  return (
    <Toggle
      id={id}
      pressed={checked}
      onPressedChange={onCheckedChange}
      disabled={disabled}
      variant="outline"
      className={className}
      aria-label={hint ?? onLabel ?? offLabel}
      title={hint}
    >
      {icon && <Icon icon={icon} />} {checked ? onLabel : offLabel}
    </Toggle>
  );
}
export interface DatePickerProps {
  value?: Date;
  onValueChange?: (value: Date | undefined) => void;
}
export function DatePicker({value, onValueChange}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline">
          {value ? value.toLocaleDateString() : 'Pick a date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(next) => {
            onValueChange?.(next);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
